import { useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { flaggedItemsData, FlaggedItem } from "../../../data/menuOversightData";
import Pagination from "../../../components/Pagination";
import LoadingSpinner from "../../../components/elements/LoadingSpinner";

const FlaggedItemsTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter flagged items based on search and type
  const filteredItems = flaggedItemsData.filter((item) => {
    const matchesSearch = item.restaurant.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.suspensionReason.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || item.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Pagination logic
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const handlePagination = (page: number) => {
    setCurrentPage(page);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleClearFlag = (itemId: string) => {
    console.log("Clear flag for item:", itemId);
    // This would normally make an API call
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "menu_item":
        return "text-blue-600 bg-blue-100";
      case "promotion":
        return "text-purple-600 bg-purple-100";
      case "content":
        return "text-orange-600 bg-orange-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case "menu_item":
        return "Menu Item";
      case "promotion":
        return "Promotion";
      case "content":
        return "Content";
      default:
        return type;
    }
  };

  return (
    <div className="w-full">
      {/* Search and Filter Section */}
      <div className="my-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 pl-10 text-sm w-full focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
            placeholder="Search flagged items..."
          />
        </div>

        {/* Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          >
            <option value="all">All Types</option>
            <option value="menu_item">Menu Item</option>
            <option value="promotion">Promotion</option>
            <option value="content">Content</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm || typeFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setTypeFilter("all");
            }}
            className="text-sm text-secondary hover:text-secondary-dark underline"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="min-h-[400px]">
        <table className="w-full">
          <thead className="font-normal">
            <tr className="bg-white">
              <th className="p-3 font-medium text-left">Restaurant ID</th>
              <th className="p-3 font-medium text-left">Suspension Reason</th>
              <th className="p-3 font-medium text-left">Suspension Date</th>
              <th className="p-3 font-medium text-left">Type</th>
              <th className="p-3 font-medium text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length > 0 ? (
              paginatedItems.map((item) => (
                <tr key={item.id} className="border text-sm hover:bg-gray-50">
                  <td className="p-5">{item.restaurant}</td>
                  <td className="p-5">{item.suspensionReason}</td>
                  <td className="p-5">{item.suspensionDate}</td>
                  <td className="p-5">
                    <span
                      className={`${getTypeColor(
                        item.type
                      )} text-xs font-medium px-2 py-1 rounded`}
                    >
                      {getTypeText(item.type)}
                    </span>
                  </td>
                  <td className="p-5">
                    <button
                      onClick={() => handleClearFlag(item.id)}
                      className="text-secondary underline hover:text-secondary-dark transition-colors"
                    >
                      Clear Flag
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-8">
                  <div className="text-gray-500">
                    {searchTerm || typeFilter !== "all"
                      ? "No flagged items match your filters."
                      : "No flagged items found."}
                    {(searchTerm || typeFilter !== "all") && (
                      <div className="mt-2">
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setTypeFilter("all");
                          }}
                          className="text-secondary hover:underline text-sm"
                        >
                          Clear filters to see all flagged items
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredItems.length > 0 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            length={totalItems}
            currentPage={currentPage}
            handlePagination={handlePagination}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        </div>
      )}
    </div>
  );
};

export default FlaggedItemsTable;